/** A numbered, linkable source. `id` is the number an inline `[n]` marker points at; `url` is
 * the only other field a producer must supply, because a pipeline may know nothing else about
 * where a claim came from. */
export type CitedSource = {
  kind?: "web";
  id: number;
  url: string;
  title?: string;
  publisher?: string;
  published_date?: string;
  accessed_date?: string;
};

/** A page of one of the domain's own uploaded documents.
 *
 * No `url`: there is nothing to link to. A reader follows this by reading the file they already
 * have, or by looking at `preview_image` when the passage was a slide whose numbers exist in the
 * picture and nowhere in the document's text. */
export type CitedDocumentSource = {
  kind: "document";
  id: number;
  source_file: string;
  page?: number | null;
  doc_title?: string | null;
  timeline?: string | null;
  is_image?: boolean;
  preview_image?: string | null;
};

/** Anything a `[1]` marker in authored text can resolve to: a web page or a document page.
 *
 * A row with no `kind` is a web source — every source persisted before document context existed
 * is one, and `isDocumentSource` treats the tag as the only thing that makes a row a document.
 *
 * Kept as a separate name from `CitedSource` rather than replacing it, so that callers holding
 * only web sources keep the precise type and every existing read of `.url` still type-checks.
 * Only the citation renderers widen to this. */
export type CitationSource = CitedSource | CitedDocumentSource;

/**
 * Inline source citations in authored text.
 *
 * A model writes references the way a report does — `[1]`, `[1][2]`, sometimes
 * `[1, 2]` — inside any field it pleases: an insight, a `why` driver, a business-impact bullet,
 * a "how it was generated" step, an evidence check. Nothing here is section-specific: text goes
 * in, a flat list of text and citation segments comes out, and the renderer resolves each id
 * against the caller's source list.
 *
 * Segments are plain data (strings and numbers) that the renderer passes to React as text and
 * as an `href` it has already checked, so there is no markup to sanitise.
 */

export type CitedTextSegment =
  | { kind: "text"; text: string }
  | { kind: "citation"; sourceId: number };

/** `[1]`, `[12]`, and the comma-separated `[1, 2]` form. Digits only, so `[Unknown]` — which
 * shows up in warehouse text — is left alone. */
const CITATION_MARKER_PATTERN = /\[\s*(\d+(?:\s*,\s*\d+)*)\s*\]/g;

/** The same marker plus the space that precedes it, so stripping never leaves a gap mid-sentence. */
const CITATION_MARKER_WITH_LEADING_SPACE_PATTERN = /[ \t]*\[\s*\d+(?:\s*,\s*\d+)*\s*\]/g;

/** Only these reach an `<a href>` — an authored `javascript:` or `data:` URL is not a source. */
const SAFE_SOURCE_URL_PROTOCOLS = ["http:", "https:"];

export function isSafeSourceUrl(url: string): boolean {
  try {
    return SAFE_SOURCE_URL_PROTOCOLS.includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

/** Splits authored text into its prose and its citation markers, in order.
 *
 * `[1, 2]` yields one citation segment per id, so a grouped marker renders as two separate
 * links rather than one link to the first source. */
export function parseCitedText(text: string): CitedTextSegment[] {
  const segments: CitedTextSegment[] = [];
  let sliceStart = 0;

  // The pattern is module-level and `/g`, so `lastIndex` survives between calls — reset it or a
  // second call would resume mid-string and drop the leading citations.
  CITATION_MARKER_PATTERN.lastIndex = 0;
  let match = CITATION_MARKER_PATTERN.exec(text);
  while (match !== null) {
    if (match.index > sliceStart) {
      segments.push({ kind: "text", text: text.slice(sliceStart, match.index) });
    }
    for (const rawId of match[1].split(",")) {
      segments.push({ kind: "citation", sourceId: Number(rawId.trim()) });
    }
    sliceStart = match.index + match[0].length;
    match = CITATION_MARKER_PATTERN.exec(text);
  }

  if (sliceStart < text.length) {
    segments.push({ kind: "text", text: text.slice(sliceStart) });
  }
  return segments;
}

export function hasCitationMarkers(text: string): boolean {
  CITATION_MARKER_PATTERN.lastIndex = 0;
  return CITATION_MARKER_PATTERN.test(text);
}

/** Text with the markers removed, for compact surfaces (cards, list previews) that show no
 * source list and would otherwise render a dangling `[1][2]` nobody can follow. */
export function stripCitationMarkers(text: string): string {
  return text.replace(CITATION_MARKER_WITH_LEADING_SPACE_PATTERN, "").trim();
}

/** Generic in the source type so a caller holding only web sources gets a map of web sources
 * back, rather than having every lookup widened to the union it never put in. */
export function sourcesById<SourceT extends CitationSource>(
  sources: SourceT[] | undefined,
): Map<number, SourceT> {
  const index = new Map<number, SourceT>();
  for (const source of sources ?? []) {
    // First one wins: a duplicated id is an authoring slip, and the earlier entry is the one the
    // text was numbered against.
    if (!index.has(source.id)) {
      index.set(source.id, source);
    }
  }
  return index;
}

/** Sources in citation order, so `[1]` is the first row of the list regardless of array order. */
export function sortSourcesById<SourceT extends CitationSource>(sources: SourceT[]): SourceT[] {
  return [...sources].sort((first, second) => first.id - second.id);
}

/** The bare domain, as a short stand-in label when a source carries no title. */
export function sourceHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Whether this source is a page of one of the domain's own documents rather than a web page.
 *
 * Keyed on the explicit tag, never on the absence of `url`: every source persisted before
 * document context existed carries no tag, and reading "untagged" as "document" would turn all
 * of them into unclickable rows. */
export function isDocumentSource(source: CitationSource): source is CitedDocumentSource {
  return source.kind === "document";
}

/** What to call a source in a link or a tooltip.
 *
 * For a web source, `id` and `url` are the only required fields — everything else is optional
 * metadata a pipeline may not have — so the hostname is the label whenever there is no usable
 * title. Checked for emptiness rather than for `undefined`: a `""` title reaching an
 * un-normalized caller would otherwise render a link with no text at all.
 *
 * For a document, the file name IS the label. The document's own title is shown beside it in the
 * meta line instead of replacing it, because the file name is what the reader has on disk. */
export function sourceLabel(source: CitationSource): string {
  if (isDocumentSource(source)) {
    return source.source_file.trim() || source.doc_title?.trim() || "document";
  }
  return source.title?.trim() || sourceHostname(source.url);
}

/** How a document citation reads inline in prose: the file name and the page, e.g.
 * `Q1 Review.pdf · p. 7`.
 *
 * Shown in place of a `[n]` number, so it names the file the reader already holds rather than a
 * number they would have to look up. The page rides along because two citations of the same file
 * are otherwise indistinguishable. */
export function documentCitationLabel(source: CitedDocumentSource): string {
  const label = sourceLabel(source);
  return typeof source.page === "number" ? `${label} · p. ${source.page}` : label;
}

/** The line shown under a document source: which document, which period, which page.
 *
 * Empty when the document carries none of them — the file name is already the label above, and
 * repeating it under itself says nothing. */
export function documentSourceMetaLine(source: CitedDocumentSource): string {
  return [
    source.doc_title?.trim() && source.doc_title.trim() !== source.source_file.trim()
      ? source.doc_title.trim()
      : undefined,
    source.timeline?.trim() || undefined,
    typeof source.page === "number" ? `p. ${source.page}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
}
